export interface RandomByteSource {
  getBytes(length: number): Promise<Uint8Array>;
}

const UINT32_BYTES = 4;
const UINT32_RANGE = 0x1_0000_0000;

const isPositiveInteger = (value: number): boolean => Number.isInteger(value) && value > 0;

const readUint32 = (bytes: Uint8Array): number => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return view.getUint32(0, false);
};

export async function uniformIndex(
  source: RandomByteSource,
  maxExclusive: number,
): Promise<number> {
  if (!isPositiveInteger(maxExclusive)) {
    throw new RangeError('maxExclusive must be a positive integer.');
  }

  if (maxExclusive > UINT32_RANGE) {
    throw new RangeError(`maxExclusive must be <= ${UINT32_RANGE}.`);
  }

  const unbiasedUpperBound = Math.floor(UINT32_RANGE / maxExclusive) * maxExclusive;

  for (;;) {
    const candidateBytes = await source.getBytes(UINT32_BYTES);

    if (candidateBytes.byteLength !== UINT32_BYTES) {
      throw new Error('RandomByteSource.getBytes(4) must resolve exactly 4 bytes.');
    }

    const candidate = readUint32(candidateBytes);

    if (candidate < unbiasedUpperBound) {
      return candidate % maxExclusive;
    }
  }
}

export async function uniformBoolean(source: RandomByteSource): Promise<boolean> {
  return (await uniformIndex(source, 2)) === 1;
}
